import React, { useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = forwardRef(({
  pdfUrl,
  selectedDocument,
  currentPage,
  onPageChange,
  loading,
  error
}, ref) => {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageWidth, setPageWidth] = useState(null);
  const [fitToWidth, setFitToWidth] = useState(true);
  const containerRef = useRef(null);
  const pageRefs = useRef({});

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    if (onPageChange) {
      onPageChange(1, numPages);
    }
  }, [onPageChange]);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
  }, []);

  const goToPage = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= numPages) {
      if (onPageChange) {
        onPageChange(pageNumber, numPages);
      }

      // Scroll to the page with proper offset calculation
      const pageElement = pageRefs.current[pageNumber];
      const container = containerRef.current;

      if (pageElement && container) {
        // Calculate the position of the page relative to the container
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();

        // Calculate scroll position with a small offset for better visibility
        const scrollOffset = pageRect.top - containerRect.top + container.scrollTop - 20;

        // Smooth scroll to the calculated position
        container.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [numPages, onPageChange]);

  // Expose goToPage to parent via ref
  useImperativeHandle(ref, () => ({
    goToPage
  }), [goToPage]);

  const nextPage = () => {
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    goToPage(currentPage - 1);
  };

  const zoomIn = () => {
    setFitToWidth(false);
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setFitToWidth(false);
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setFitToWidth(false);
    setScale(1.0);
  };

  const handleFitToWidth = () => {
    setFitToWidth(true);
  };

  const onPageLoadSuccess = (page) => {
    setPageLoading(false);
    // Get the page dimensions for fit-to-width calculation
    if (!pageWidth && page) {
      setPageWidth(page.originalWidth);
    }
  };

  // Calculate scale for fit-to-width
  useEffect(() => {
    if (fitToWidth && containerRef.current && pageWidth) {
      const containerWidth = containerRef.current.offsetWidth;
      // Subtract some padding (40px total) to prevent horizontal scrollbar
      const availableWidth = containerWidth - 60;
      const calculatedScale = availableWidth / pageWidth;
      setScale(Math.max(0.5, Math.min(calculatedScale, 3.0)));
    }
  }, [fitToWidth, pageWidth, containerRef.current?.offsetWidth]);

  const onPageLoadError = (error) => {
    console.error('Page load error:', error);
    setPageLoading(false);
  };

  if (!selectedDocument && !pdfUrl) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-header">
          <h3>PDF Viewer</h3>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Select a document to view
          </div>
        </div>
        <div className="pdf-content">
          <div className="empty-state">
            📄<br/>
            No document selected.<br/>
            Click on a document from the list to view it here.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-header">
          <h3>PDF Viewer</h3>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Loading document...
          </div>
        </div>
        <div className="pdf-content">
          <div className="pdf-loading">
            Loading PDF...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-header">
          <h3>PDF Viewer</h3>
          <div style={{ fontSize: '0.9rem', color: '#ff4757' }}>
            Error loading document
          </div>
        </div>
        <div className="pdf-content">
          <div className="pdf-error">
            <div>
              ❌<br/>
              Failed to load PDF<br/>
              <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                {error}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      <div className="pdf-header">
        <h3>
          {selectedDocument?.document_name || 'PDF Document'}
        </h3>
        
        {numPages && (
          <div className="pdf-controls">
            <span className="page-info">
              {numPages} page{numPages !== 1 ? 's' : ''} total
            </span>
            
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={zoomOut}
                disabled={scale <= 0.5}
                title="Zoom out"
              >
                −
              </button>

              <button
                onClick={resetZoom}
                title="Reset zoom to 100%"
                style={{ minWidth: '60px' }}
              >
                {Math.round(scale * 100)}%
              </button>

              <button
                onClick={zoomIn}
                disabled={scale >= 3.0}
                title="Zoom in"
              >
                +
              </button>

              <button
                onClick={handleFitToWidth}
                title="Fit page width to viewer"
                className={fitToWidth ? 'active' : ''}
                style={{
                  minWidth: '80px',
                  background: fitToWidth ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : undefined,
                  color: fitToWidth ? 'white' : undefined
                }}
              >
                Fit Width
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pdf-content" ref={containerRef}>
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="pdf-loading">
                Loading PDF document...
              </div>
            }
            error={
              <div className="pdf-error">
                Failed to load PDF.<br/>
                The file might be corrupted or unsupported.
              </div>
            }
          >
            {/* Render all pages */}
            {numPages && Array.from(new Array(numPages), (el, index) => {
              const pageNum = index + 1;
              return (
                <div
                  key={`page_${pageNum}`}
                  className="pdf-page-container"
                  ref={(el) => { pageRefs.current[pageNum] = el; }}
                >
                  <div className="pdf-page-header">
                    Page {pageNum} of {numPages}
                  </div>
                  <Page
                    pageNumber={pageNum}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onLoadSuccess={onPageLoadSuccess}
                    onLoadError={onPageLoadError}
                    loading={
                      <div className="pdf-loading">
                        Loading page {pageNum}...
                      </div>
                    }
                    error={
                      <div className="pdf-error">
                        Failed to load page {pageNum}
                      </div>
                    }
                    className="pdf-page"
                  />
                </div>
              );
            })}
          </Document>
        ) : (
          <div className="pdf-loading">
            Preparing PDF for viewing...
          </div>
        )}
      </div>
    </div>
  );
});

PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;